/**
 * Fitment integration for <cart-drawer-suggestions>, split out so the component
 * stays focused on loading/merging/rendering recommendations (single
 * responsibility). Every read is feature-detected against the RPM Fitment app
 * globals (RpmMachineContext, RpmCartDOM, rpmFitmentData); if the app is absent
 * all methods no-op and the recs behave as if there were no fitment data.
 *
 * Loaded before element.cart-drawer-suggestions.js (theme.liquid); the component
 * instantiates one of these and delegates filtering, badging, and the fit-based
 * default variant to it.
 */
class CartSuggestionsFitment {
  // The customer's actively-selected machine, gated by the shared
  // RpmMachineContext. Recs filter/badge against this ONE machine (not the whole
  // garage) so a dealer with many saved machines sees only the one they're on.
  activeMachine() {
    const ctx = window.RpmMachineContext
    if (!ctx || typeof ctx.has !== 'function' || !ctx.has()) return null
    const a = typeof ctx.getActiveMachine === 'function' ? ctx.getActiveMachine() : null
    if (!a || !a.metaobjectId) return null
    return { metaobjectId: String(a.metaobjectId).trim(), displayName: a.displayName || '' }
  }

  activeMachineSet() {
    const m = this.activeMachine()
    return m ? new Set([m.metaobjectId]) : null
  }

  universalFitGid() {
    const data = window.rpmFitmentData
    return data && data.universalFitGid ? String(data.universalFitGid).trim() : null
  }

  cardMachineIds(card) {
    const parsed = this._parseJson(card.getAttribute('data-machine-ids'))
    return Array.isArray(parsed) ? parsed.map(id => String(id).trim()) : []
  }

  // Keep cards with no fitment data (neutral) and universal-fit cards; otherwise
  // require an intersection with the active machine. Universal is detected from
  // the server-computed data-universal flag (authoritative handle check) AND the
  // client universalFitGid, so a missing client global can't hide universal parts.
  cardFits(card, fitSet) {
    if (card.getAttribute('data-universal') === 'true') return true
    const ids = this.cardMachineIds(card)
    if (ids.length === 0) return true
    const uni = this.universalFitGid()
    if (uni && ids.indexOf(uni) !== -1) return true
    return ids.some(id => fitSet.has(id))
  }

  // Drop every product that doesn't fit the active machine. Strict: if nothing
  // fits, the grid is left empty and the recs section hides (the component then
  // tries the next intent, then hides). A fitment-layer error fails open (returns
  // true for that card) so an exception can never wrongly hide a rec.
  applyFitFilter(grid) {
    let fitSet = null
    try { fitSet = this.activeMachineSet() } catch (e) { fitSet = null }
    if (!fitSet) return
    const cards = Array.from(grid.children)
    const fitting = cards.filter(card => {
      try { return this.cardFits(card, fitSet) } catch (e) { return true }
    })
    if (fitting.length === cards.length) return
    cards.forEach(card => { if (fitting.indexOf(card) === -1) card.remove() })
  }

  // Badge + fit-default the variant for every committed card. Runs after the grid
  // is visible, each card in its own try/catch, so fitment can never break a rec.
  applyToCards(slot, setCardPrice) {
    if (!slot) return
    let hasCtx = false
    try {
      hasCtx = !!(window.RpmMachineContext
        && typeof window.RpmMachineContext.has === 'function'
        && window.RpmMachineContext.has())
    } catch (e) { hasCtx = false }
    slot.querySelectorAll('.cart-suggestion-card').forEach(card => {
      try {
        this.applyDefaultVariant(card, setCardPrice)
        this._badgeCard(card, hasCtx)
      } catch (e) { /* additive — never let it break a rec card */ }
    })
  }

  // Reuse the app's own badge factory so the chip is identical to cart line items
  // (including its deliberately muted no-fit/verify styling — never red).
  _badgeCard(card, hasCtx) {
    const existing = card.querySelector('[data-rpm-cart-badge]')
    if (existing) existing.remove()
    if (!hasCtx || !window.RpmCartDOM || typeof window.RpmCartDOM.createBadgeElement !== 'function') return
    if (card.getAttribute('data-universal') === 'true') return
    const ids = this.cardMachineIds(card)
    if (ids.length === 0) return
    const uni = this.universalFitGid()
    if (uni && ids.indexOf(uni) !== -1) return
    const result = this.fitState(ids)
    if (result.state === 'none') return
    const badge = window.RpmCartDOM.createBadgeElement(result.state, result.machineName, null)
    const title = card.querySelector('.cart-suggestion-card__title')
    if (title && title.parentNode) title.parentNode.insertBefore(badge, title.nextSibling)
  }

  // Badge against the active machine: surviving recs are pre-filtered to fit it,
  // so this resolves to 'fits' with that machine's name (no-fit is a defensive
  // fallback only, since non-fitting cards are already removed).
  fitState(ids) {
    const m = this.activeMachine()
    if (m && ids.indexOf(m.metaobjectId) !== -1) {
      return { state: 'fits', machineName: m.displayName }
    }
    return { state: 'no-fit', machineName: null }
  }

  // For multi-variant cards with variant-level fitment: hide the variant options
  // that don't fit the active machine, then default the <select> to a fitting one
  // and sync the displayed price.
  applyDefaultVariant(card, setCardPrice) {
    const select = card.querySelector('select[name="id"]')
    if (!select) return
    const map = this._parseJson(select.getAttribute('data-variant-fitment-map'))
    if (!map || !map.values) return
    const fitSet = this.activeMachineSet()
    if (!fitSet) return
    this._hideNonFittingOptions(select, map.values, fitSet)
    const fittingValue = this._fittingOptionValue(map.values, fitSet)
    if (fittingValue !== null) {
      const target = this._optionByFitValue(select, fittingValue)
      if (target && select.value !== target.value) select.value = target.value
    }
    if (typeof setCardPrice === 'function') setCardPrice(card, select.options[select.selectedIndex])
  }

  // Remove variant <option>s that don't fit the active machine. Options with no
  // fit value, options not in the fitment map, and universal options are kept;
  // never removes every option (the product passed product-level fit, so >=1 fits).
  _hideNonFittingOptions(select, values, fitSet) {
    const uni = this.universalFitGid()
    const opts = Array.from(select.options)
    const toRemove = opts.filter(opt => {
      const fv = opt.getAttribute('data-fit-value')
      if (fv == null) return false
      const gids = this._gidsForValue(values, fv)
      if (gids === null) return false
      if (uni && gids.indexOf(uni) !== -1) return false
      return !gids.some(g => fitSet.has(String(g).trim()))
    })
    if (toRemove.length >= opts.length) return
    toRemove.forEach(opt => opt.remove())
  }

  // Case-insensitive lookup of an option value's machine GIDs in the fitment map.
  _gidsForValue(values, value) {
    if (values[value]) return values[value]
    const norm = String(value).trim().toLowerCase()
    for (const k in values) {
      if (Object.prototype.hasOwnProperty.call(values, k) && k.trim().toLowerCase() === norm) return values[k]
    }
    return null
  }

  _fittingOptionValue(values, fitSet) {
    for (const ov in values) {
      if (!Object.prototype.hasOwnProperty.call(values, ov)) continue
      const gids = values[ov]
      if (Array.isArray(gids) && gids.some(g => fitSet.has(String(g).trim()))) return ov
    }
    return null
  }

  _optionByFitValue(select, fittingValue) {
    const target = String(fittingValue).trim().toLowerCase()
    const opts = select.options
    for (let i = 0; i < opts.length; i++) {
      const fv = opts[i].getAttribute('data-fit-value')
      if (fv != null && fv.trim().toLowerCase() === target) return opts[i]
    }
    return null
  }

  _parseJson(value) {
    if (!value) return null
    try { return JSON.parse(value) } catch (e) { return null }
  }
}
